import { useCallback, useRef } from 'react';
import { useWorkspaceStore, setWorkspaces, setCurrentWorkspace, setGitAccount } from '../store/workspaceStore';
import { setCollections, setSchemas } from '../store/collectionsStore';
import {
  useVariablesStore,
  setGlobalVariables,
  setWorkspaceVariables,
  setEnvironments,
  setSelectedEnvironment,
} from '../store/variablesStore';
import { blockedByUnreadable } from '../services/gitUnreadable';
import i18n from '../i18n';
import { setGitConnectWorkspace, setGitConflict, setGitSecrets, useUiModalStore } from '../store/uiModalStore';
import { buildLocalManifestEntries } from '../utils/workspaceStorage';
import { dedupeCollectionRequestIds } from '../utils/localIds';
import { normalizeSchemas } from '../utils/schemaTree';
import { loadWorkspaceSnapshot, saveWorkspaceSnapshot } from '../services/workspaceStoreRouter';
import { saveLocalWorkspaceManifest } from '../services/localWorkspaceStore';
import {
  readMirrorSnapshot as gitReadMirrorSnapshot,
  pushWorkspace as gitPushWorkspace,
  refreshMirror as gitRefreshMirror,
  pullWorkspace as gitPullWorkspace,
  connectRepo as gitConnectRepo,
  resolveConflicts as gitResolveConflicts,
} from '../services/workspaceGitSync';
import { setWorkspaceGitLink } from '../services/accountsStore';
import { noteGitSync, isLocalOnly, gitWords } from '../services/git/gitSyncStatus';
import { useGitLocal } from './useGitLocal';
import { isOfflineError } from '../services/git/offlineQueue';
import { providerOf, getProvider } from '../services/git/providers';
import { loadGitChanges } from '../services/git/gitChanges';
import { putBackVersion } from '../services/git/gitHistory';
import { loadItemHistory } from '../services/git/itemHistory';
import { refreshOpenTabsFrom } from '../services/git/refreshOpenTabs';
import { resolveJoinConflicts } from '../services/git/joinRemote';
import { useNotificationStore } from '../store/notificationStore';
import { notifyGit } from './workspaceGitNotify';
import { isVaultLockedError, waitForVaultGate, VAULT_LOCKED_SYNC_MESSAGE } from '../services/vaultGate';

/** Git actions on a workspace: sync, pull, connect, history, put back, conflicts. Track-on-this-device links go through useGitLocal. */
export function useWorkspaceGitActions({
  buildLiveSnapshot,
  toastT,
  setGitSyncingWorkspaceId,
}) {
  // Build the live snapshot for the workspace currently in view.
  // The data a git action writes into a workspace's mirror. The live stores hold
  // the OPEN workspace only: for any other workspace (Git page, workspace menu)
  // its own saved snapshot is used. Before 01.10.2026 this always returned the
  // open workspace's data — pushing another workspace sent the wrong one.
  const snapshotForGit = useCallback(async (workspace) => {
    const current = useWorkspaceStore.getState().currentWorkspace;
    if (!workspace || !current || String(workspace.id) === String(current.id)) return buildLiveSnapshot();
    return loadWorkspaceSnapshot(workspace);
  }, [buildLiveSnapshot]);

  // "Commit anyway" from the secret-scan modal (card 5) re-runs the CURRENT
  // handler with allowSecrets — refs, so the stored callback never goes stale.
  const handleGitSyncRef = useRef(null);
  const handleGitConnectRef = useRef(null);
  const handleGitPullRef = useRef(null);

  // Apply a snapshot read back from the mirror (after pull/merge) into app state.
  const applyMirrorSnapshot = useCallback((workspace, snap) => {
    if (!snap) return;
    // A pulled file that does not read would be saved as "deleted" below.
    if (blockedByUnreadable(snap)) return;
    const nextCollections = dedupeCollectionRequestIds(snap.collections || []);
    setCollections(nextCollections);
    // Open tabs show the applied version (Restore, Discard, a pull) unless they hold unsaved edits.
    refreshOpenTabsFrom(nextCollections);
    setSchemas(normalizeSchemas(snap.schemas));
    setGlobalVariables(snap.globalVariables || []);
    setWorkspaceVariables(snap.workspaceVariables || []);
    const envs = snap.environments || [];
    setEnvironments(envs);
    // The active environment is a device preference (git-sync audit
    // 01.10.2026): a pull keeps this device's choice while it still exists.
    // Only when it is gone does the repo's old `selected_environment` (still
    // in repos written before that date) pick one.
    const current = useVariablesStore.getState().selectedEnvironment;
    const kept = current ? envs.find((e) => String(e.id) === String(current.id)) : null;
    const sel =
      kept ||
      (snap.selectedEnvironmentId
        ? envs.find((e) => String(e.id) === String(snap.selectedEnvironmentId))
        : null);
    setSelectedEnvironment(sel || null);
    // Persist the merged result back to the workspace's primary store too.
    saveWorkspaceSnapshot(workspace, snap, { skipEmptyArrays: false }).catch(() => {});
  }, []);

  // What a pull / merge / resolve left in the mirror, into the workspace: the
  // view when it is the open one, its saved snapshot otherwise (before
  // 01.10.2026 a non-open workspace's pull never reached its store, and the
  // next push sent the stale data back over it).
  const applyMirrorToWorkspace = useCallback(async (workspace) => {
    const snap = await gitReadMirrorSnapshot(workspace);
    if (!snap || blockedByUnreadable(snap)) return;
    if (String(useWorkspaceStore.getState().currentWorkspace?.id) === String(workspace.id)) {
      applyMirrorSnapshot(workspace, snap);
    } else {
      await saveWorkspaceSnapshot(workspace, snap, { skipEmptyArrays: false });
    }
  }, [applyMirrorSnapshot]);

  // Persist a workspace's git link metadata to state + local manifest + accounts store.
  const updateWorkspaceGitMeta = useCallback((workspaceId, git) => {
    setWorkspaces((prev) => {
      const next = prev.map((w) =>
        String(w.id) === String(workspaceId) ? { ...w, git } : w
      );
      saveLocalWorkspaceManifest(buildLocalManifestEntries(next)).catch(() => {});
      return next;
    });
    setCurrentWorkspace((cw) =>
      cw && String(cw.id) === String(workspaceId) ? { ...cw, git } : cw
    );
    setWorkspaceGitLink(workspaceId, git).catch(() => {});
  }, []);

  // Track on this device (T3/T6/T7): local links are handled in their own hook.
  const gitLocal = useGitLocal({
    toastT,
    snapshotForGit,
    updateWorkspaceGitMeta,
    applyMirrorToWorkspace,
    setGitSyncingWorkspaceId,
  });

  /* ── Git sync ──────────────────────────────────────────────────────────── */
  // Primary GitHub action: connect if not linked yet, otherwise push the latest state.
  // `fromQueue`: the offline queue retrying on its own (useGitOfflineQueue) —
  // it never opens a dialog nobody asked for; a decision it needs (secrets,
  // conflicts, sign-in) becomes "press Sync now" on the Git page instead.
  const handleGitSync = useCallback(async (workspace, { allowSecrets = false, fromQueue = false } = {}) => {
    if (!workspace?.id) return;
    // Tracked only on this device: "Save version" — no account, no network.
    if (isLocalOnly(workspace)) {
      await gitLocal.saveLocalVersion(workspace, { allowSecrets, fromQueue });
      return;
    }
    // Signed in where THIS workspace syncs (GitHub, a GitLab account, …) — card 6.
    let account = { connected: false };
    if (workspace.git) {
      try {
        account = await providerOf(workspace).getAccount(workspace.git);
      } catch {
        account = { connected: false };
      }
    }
    setGitAccount(await getProvider('github').getAccount().catch(() => ({ connected: false, login: '', avatarUrl: '' })));
    if (!account.connected || !workspace.git) {
      if (fromQueue) noteGitSync(workspace, { error: 'Sign in again, then press Sync now' });
      else setGitConnectWorkspace(workspace);
      return;
    }
    // Already connected → push.
    setGitSyncingWorkspaceId(workspace.id);
    try {
      const result = await gitPushWorkspace(workspace, await snapshotForGit(workspace), { allowSecrets });
      if (fromQueue && (result.status === 'secrets' || result.status === 'conflicts')) {
        noteGitSync(workspace, { error: 'Needs your decision — press Sync now' });
      } else if (result.status === 'secrets') {
        // Card 5: shown file:line + rule; pushing anyway is a typed decision.
        setGitSecrets({
          workspace,
          findings: result.findings,
          commitAnyway: () => handleGitSyncRef.current?.(workspace, { allowSecrets: true }),
        });
      } else if (result.status === 'conflicts') {
        setGitConflict({ workspace, conflicts: result.conflicts || [] });
      } else if (result.status === 'ok') {
        noteGitSync(workspace, { ok: true });
        toastT('toast.gitPushed', 'success', { name: workspace.name });
      } else {
        noteGitSync(workspace, { error: 'The remote moved on — pull, then sync again' });
        toastT('toast.gitPushFailed', 'error', { name: workspace.name });
        if (!fromQueue) notifyGit('error', workspace, i18n.t('toast.gitRemoteMoved'));
      }
    } catch (err) {
      if (isVaultLockedError(err)) {
        // The token is in the Vault and the Vault is locked. The queue's own
        // retries only note it ("Unlock the Vault to sync", no toast, no
        // dialog); a click on Sync now asks for the Vault and syncs after.
        noteGitSync(workspace, { error: VAULT_LOCKED_SYNC_MESSAGE });
        if (!fromQueue && (await waitForVaultGate('git-sync'))) {
          setGitSyncingWorkspaceId(null);
          await handleGitSyncRef.current?.(workspace, { allowSecrets });
        }
        return;
      }
      if (isOfflineError(err)) {
        // The commit is made; only the push waits (set 7d).
        noteGitSync(workspace, { queued: true });
        if (!fromQueue) {
          toastT('toast.gitQueuedOffline', 'info', { name: workspace.name });
          notifyGit('info', workspace);
        }
      } else {
        noteGitSync(workspace, { error: err?.message });
        toastT('toast.gitPushFailed', 'error', { message: err?.message || '' });
        if (!fromQueue) notifyGit('error', workspace, err?.message || '');
      }
    } finally {
      setGitSyncingWorkspaceId(null);
    }
  }, [snapshotForGit, toastT, setGitSyncingWorkspaceId, gitLocal]);
  handleGitSyncRef.current = handleGitSync;

  // Git page (card 7, set 7b): what changed since the last sync, from the live state.
  const handleGitChanges = useCallback(
    async (workspace) => loadGitChanges(workspace, await snapshotForGit(workspace)),
    [snapshotForGit]
  );

  // Git page (card 7, set 7c): Discard a change / Restore a version of one file.
  // The mirror is brought up to date with the live app first (nothing newer is
  // lost), the file version is written, and the mirror is applied back — to the
  // view when it is the open workspace, to its saved snapshot otherwise.
  const handleGitPutBack = useCallback(async (workspace, path, content, mode = 'restore') => {
    try {
      await putBackVersion({
        workspace,
        path,
        content,
        live: await snapshotForGit(workspace),
        refreshMirror: gitRefreshMirror,
        readMirror: gitReadMirrorSnapshot,
        blocked: blockedByUnreadable,
        apply: (snap) =>
          useWorkspaceStore.getState().currentWorkspace?.id === workspace.id
            ? applyMirrorSnapshot(workspace, snap)
            : saveWorkspaceSnapshot(workspace, snap, { skipEmptyArrays: false }),
      });
      useNotificationStore.getState().success(
        mode === 'discard' ? `Back to how it was at ${gitWords(workspace).last}.` : 'The version is back in Apiboo.',
        mode === 'discard' ? 'Change discarded' : 'Version restored'
      );
    } catch (err) {
      useNotificationStore.getState().error(err?.message || 'Could not put that version back.', 'Nothing changed');
      throw err;
    }
  }, [snapshotForGit, applyMirrorSnapshot]);

  // Request editor's History (card 9): one item's syncs, renames followed.
  const handleGitItemHistory = useCallback(
    async (workspace, itemId) => loadItemHistory(workspace, await snapshotForGit(workspace), itemId),
    [snapshotForGit]
  );

  const handleGitPull = useCallback(async (workspace) => {
    // A local history has no remote to pull from.
    if (!workspace?.git || isLocalOnly(workspace)) return;
    setGitSyncingWorkspaceId(workspace.id);
    try {
      const result = await gitPullWorkspace(workspace);
      if (result.status === 'conflicts') {
        setGitConflict({ workspace, conflicts: result.conflicts || [] });
      } else if (result.status === 'up_to_date') {
        noteGitSync(workspace, { ok: true });
        toastT('toast.gitUpToDate', 'info', { name: workspace.name });
      } else {
        // fast_forward or merged → reload from mirror into the active view.
        await applyMirrorToWorkspace(workspace);
        noteGitSync(workspace, { ok: true });
        toastT('toast.gitPulled', 'success', { name: workspace.name });
      }
    } catch (err) {
      noteGitSync(workspace, { error: err?.message });
      if (isVaultLockedError(err)) {
        if (await waitForVaultGate('git-sync')) {
          setGitSyncingWorkspaceId(null);
          await handleGitPullRef.current?.(workspace);
        }
        return;
      }
      toastT('toast.gitPullFailed', 'error', { message: err?.message || '' });
    } finally {
      setGitSyncingWorkspaceId(null);
    }
  }, [applyMirrorToWorkspace, toastT, setGitSyncingWorkspaceId]);
  handleGitPullRef.current = handleGitPull;

  const handleGitConnectConfirm = useCallback(async ({ owner, repo, isPrivate, allowSecrets = false, provider, accountId, url, vaultItemId, vaultItemName }) => {
    const workspace = useUiModalStore.getState().gitConnectWorkspace;
    if (!workspace) return;
    // "This device only (no remote)" → a local history (T3).
    if (provider === 'local') {
      await gitLocal.startTracking(workspace, { allowSecrets });
      return;
    }
    // The dialog in "Add a remote" mode, for a local history (T6).
    if (isLocalOnly(workspace)) {
      await gitLocal.addRemoteToLocal(workspace, { owner, repo, isPrivate, allowSecrets, provider, accountId, url, vaultItemId, vaultItemName });
      return;
    }
    setGitSyncingWorkspaceId(workspace.id);
    try {
      const result = await gitConnectRepo(workspace, {
        owner,
        repo,
        isPrivate,
        createIfMissing: true,
        snapshot: await snapshotForGit(workspace),
        allowSecrets,
        provider,
        accountId,
        url,
        vaultItemId,
        vaultItemName,
      });
      if (result.status === 'secrets') {
        // Nothing was committed or linked yet — keep the workspace unconnected.
        setGitSecrets({
          workspace,
          findings: result.findings,
          commitAnyway: () => handleGitConnectRef.current?.({ owner, repo, isPrivate, allowSecrets: true, provider, accountId, url, vaultItemId, vaultItemName }),
        });
        return;
      }
      updateWorkspaceGitMeta(workspace.id, result.git);
      const wsWithGit = { ...workspace, git: result.git };

      if (result.status === 'diverged') {
        // Remote already has content — merge it in, then push our workspace up.
        const merged = await gitPullWorkspace(wsWithGit);
        if (merged.status === 'conflicts') {
          // A fresh device joining: resolved on top of the remote's, not merged (card 01fee74a).
          setGitConflict({ workspace: wsWithGit, conflicts: merged.conflicts || [], join: Boolean(result.fresh) });
        } else {
          const push = await gitPushWorkspace(wsWithGit, undefined, { allowSecrets });
          await applyMirrorToWorkspace(wsWithGit);
          if (push.status === 'secrets') {
            setGitSecrets({
              workspace: wsWithGit,
              findings: push.findings,
              commitAnyway: () => handleGitSyncRef.current?.(wsWithGit, { allowSecrets: true }),
            });
          } else if (push.status === 'conflicts') {
            setGitConflict({ workspace: wsWithGit, conflicts: push.conflicts || [] });
          } else {
            noteGitSync(wsWithGit, { ok: true });
            toastT('toast.gitConnected', 'success', { name: workspace.name });
          }
        }
      } else {
        noteGitSync(wsWithGit, { ok: true });
        toastT('toast.gitConnected', 'success', { name: workspace.name });
      }
      setGitConnectWorkspace(null);
    } catch (err) {
      // The account's token is in the locked Vault: ask for it, then connect.
      if (isVaultLockedError(err) && (await waitForVaultGate('git-sync'))) {
        setGitSyncingWorkspaceId(null);
        return await handleGitConnectRef.current?.({ owner, repo, isPrivate, allowSecrets, provider, accountId, url, vaultItemId, vaultItemName });
      }
      toastT('toast.gitConnectFailed', 'error', { message: err?.message || '' });
      throw err;
    } finally {
      setGitSyncingWorkspaceId(null);
    }
  }, [snapshotForGit, updateWorkspaceGitMeta, applyMirrorToWorkspace, toastT, setGitSyncingWorkspaceId, gitLocal]);
  handleGitConnectRef.current = handleGitConnectConfirm;

  // Row actions of a local history (T6, T7): Add a remote opens the Connect
  // dialog, which sees the local link and switches to "Add a remote".
  const handleGitAddRemote = useCallback((workspace) => {
    if (isLocalOnly(workspace)) setGitConnectWorkspace(workspace);
  }, []);
  const handleGitStopTracking = gitLocal.stopTracking;

  const handleGitResolveConflicts = useCallback(async (resolutions) => {
    const ctx = useUiModalStore.getState().gitConflict;
    if (!ctx?.workspace) return;
    const workspace = ctx.workspace;
    setGitSyncingWorkspaceId(workspace.id);
    try {
      await (ctx.join ? resolveJoinConflicts : gitResolveConflicts)(workspace, resolutions);
      // Reflect the resolved files in the active view.
      await applyMirrorToWorkspace(workspace);
      noteGitSync(workspace, { ok: true });
      toastT('toast.gitResolved', 'success', { name: workspace.name });
      setGitConflict(null);
    } catch (err) {
      noteGitSync(workspace, { error: err?.message });
      toastT('toast.gitResolveFailed', 'error', { message: err?.message || '' });
    } finally {
      setGitSyncingWorkspaceId(null);
    }
  }, [applyMirrorToWorkspace, toastT, setGitSyncingWorkspaceId]);

  return {
    handleGitSync,
    handleGitChanges,
    handleGitPutBack,
    handleGitItemHistory,
    handleGitPull,
    handleGitConnectConfirm,
    handleGitAddRemote,
    handleGitStopTracking,
    handleGitResolveConflicts,
  };
}
